<template>
  <div class="concentration">
    <span>(</span>
    <md-field class="ui-theme-3 compact numerator-value">
      <md-input
        type="number"
        :value="safeConcentration.numeratorValue"
        :disabled="readonly"
        aria-label="Numerator Value"
        data-testid="numerator-value-input"
        @input="value => updateNumeratorValue(value)"
      />
    </md-field>
    <help-tooltip v-if="numeratorError" type="error">
      {{ numeratorError.message }}
    </help-tooltip>
    <md-field class="ui-theme-3 compact numerator-units">
      <md-select
        v-model="safeConcentration.numeratorUnits"
        md-dense
        :disabled="readonly"
        aria-label="Numerator Unit"
        data-testid="numerator-unit-select"
        @md-selected="units => updateNumeratorUnits(units)"
      >
        <md-option
          v-for="unit of numeratorUnits"
          :key="unit.key"
          :value="unit.key"
          :aria-label="unit.label.en"
          data-testid="numerator-unit-option"
        >
          {{ unit.label.en }}
        </md-option>
      </md-select>
    </md-field>
    <span>/</span>
    <template v-if="canEditDenominator">
      <md-field
        class="ui-theme-3 compact denominator-value"
      >
        <md-input
          type="number"
          :value="safeConcentration.denominatorValue"
          :disabled="readonly"
          aria-label="Denominator Value"
          data-testid="denominator-value-input"
          @input="value => updateDenominatorValue(value)"
        />
      </md-field>
      <help-tooltip v-if="denominatorError" type="error">
        {{ denominatorError.message }}
      </help-tooltip>
      <md-field class="ui-theme-3 compact denominator-units">
        <md-select
          v-model="safeConcentration.denominatorUnits"
          md-dense
          :disabled="readonly"
          aria-label="Denominator Unit"
          data-testid="denominator-unit-select"
          @md-selected="units => updateDenominatorUnits(units)"
        >
          <md-option
            v-for="unit of denominatorUnits"
            :key="unit.key"
            :value="unit.key"
            :aria-label="unit.label.en"
            data-testid="denominator-unit-option"
          >
            {{ unit.label.en }}
          </md-option>
        </md-select>
      </md-field>
    </template>
    <md-field
      v-if="!canEditDenominator"
      :aria-label="denominatorUnit"
      data-testid="denominator-unit"
      class="ui-theme-3 compact denominator-units"
    >
      <md-input disabled :value="denominatorUnit" />
    </md-field>
    <span>)</span>
  </div>
</template>

<script>
import { dosageForms, unitTypes } from '~lib/common/config/medication';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';

export default {
  name: 'concentration',
  components: {
    HelpTooltip
  },
  props: {
    dosageFormType: {
      type: String,
      required: true,
    },
    concentration: {
      type: Object,
      default: () => ({})
    },
    dosageFormErrors: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    dosageForm() {
      return dosageForms.find(df => df.key === this.dosageFormType);
    },
    safeConcentration() {
      return this.concentration || {};
    },
    canEditDenominator() {
      return !!(this.dosageForm && this.dosageForm.unitCategories2);
    },
    numeratorUnits() {
      if (!this.dosageForm) {
        return [];
      }
      const units = [];
      for (const unitType of unitTypes) {
        if (this.dosageForm.unitCategories1.includes(unitType.category)) {
          units.push(unitType);
        }
      }
      return units;
    },
    denominatorUnits() {
      if (!this.dosageForm) {
        return [];
      }
      const units = [];
      for (const unitType of unitTypes) {
        if (this.dosageForm.unitCategories2.includes(unitType.category)) {
          units.push(unitType);
        }
      }
      return units;
    },
    denominatorUnit() {
      if (!this.dosageForm) {
        return '';
      }
      const unit = unitTypes.find(ut => ut.key === this.dosageForm.unitType2);
      return unit ? unit.label.en : '';
    },
    numeratorError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'numeratorValue');
    },
    denominatorError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'denominatorValue');
    },
  },
  methods: {
    updateNumeratorValue(value) {
      this.emitUpdate({ numeratorValue: value });
    },
    updateNumeratorUnits(units) {
      this.emitUpdate({ numeratorUnits: units }, true);
    },
    updateDenominatorValue(value) {
      this.emitUpdate({ denominatorValue: value });
    },
    updateDenominatorUnits(units) {
      this.emitUpdate({ denominatorUnits: units }, true);
    },
    emitUpdate(updates, unitsChanged) {
      const updated = {
        ...this.concentration,
        ...updates
      }
      this.$emit('update', updated, unitsChanged);
    },
  }
}
</script>

<style scoped>
.concentration {
  display: flex;
  align-items: center;
}

.concentration .md-field {
  width: 75px;
}

.concentration >>> input {
  width: 75px;
}

.concentration span:not(:first-child) {
  margin-left: 0.5rem;
}

.concentration span:not(:last-child) {
  margin-right: 0.5rem;
}

.numerator-value,
.denominator-value {
  margin-right: 1rem !important;
}

.denominator-units {
  width: 140px !important;
}

.numerator-units {
  width: 90px !important;
}
</style>